import React from 'react';

export default function Footer() {
  // Smoothly scroll to a page section identified by its id.
  const scrollToSection = (id: string) => {
    try {
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      } else {
        console.warn(`Footer: No element found with id="${id}"`);
      }
    } catch (error) {
      console.error('Footer: scrollToSection failed', error);
    }
  };

  return (
    <footer className="bg-gradient-to-br from-slate-800 via-slate-700 to-slate-800 text-white relative overflow-hidden">
      {/* Decorative floating blobs */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-0 w-64 md:w-96 h-64 md:h-96 bg-teal-500 rounded-full blur-3xl animate-float"></div>
        <div
          className="absolute bottom-0 right-0 w-64 md:w-96 h-64 md:h-96 bg-emerald-500 rounded-full blur-3xl animate-float"
          style={{ animationDelay: '2s' }}
        ></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-12 relative z-10">
        {/* Main footer grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8 mb-8">
          {/* Logo & description */}
          <div className="col-span-2 md:col-span-1">
            <button
              onClick={() => scrollToSection('home')}
              className="flex items-center gap-2.5 mb-3 md:mb-4 cursor-pointer group"
            >
              <div className="w-9 h-9 md:w-10 md:h-10 rounded-xl bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                <i className="ri-settings-3-line text-white text-lg md:text-xl"></i>
              </div>
              <div className="flex flex-col">
                <span className="font-oswald text-base md:text-lg font-bold text-white leading-tight uppercase tracking-wide">
                  serv1ce.kz
                </span>
                <span className="text-[10px] md:text-xs text-teal-300 leading-tight">
                  Ремонт техники
                </span>
              </div>
            </button>
            <p className="text-xs md:text-sm text-gray-400 leading-relaxed">
              Профессиональный ремонт стиральных, посудомоечных и сушильных машин в
              Алматы. Быстро, надёжно и доступно.
            </p>
          </div>

          {/* Quick links */}
          <div>
            <h4 className="font-bold text-sm md:text-lg mb-3 md:mb-4 bg-gradient-to-r from-teal-400 to-emerald-400 bg-clip-text text-transparent">
              Навигация
            </h4>
            <ul className="space-y-1.5 md:space-y-2">
              {[
                { id: 'home', label: 'Главная' },
                { id: 'services', label: 'Услуги' },
                { id: 'prices', label: 'Цены' },
                { id: 'reviews', label: 'Отзывы' },
              ].map((item) => (
                <li key={item.id}>
                  <button
                    onClick={() => scrollToSection(item.id)}
                    className="text-xs md:text-sm text-gray-400 hover:text-teal-400 hover:translate-x-1 transition-all duration-300 inline-block cursor-pointer"
                  >
                    → {item.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Services list */}
          <div>
            <h4 className="font-bold text-sm md:text-lg mb-3 md:mb-4 bg-gradient-to-r from-teal-400 to-emerald-400 bg-clip-text text-transparent">
              Услуги
            </h4>
            <ul className="space-y-1.5 md:space-y-2 text-xs md:text-sm text-gray-400">
              <li className="hover:text-teal-400 transition-colors cursor-pointer">
                • Стиральные машины
              </li>
              <li className="hover:text-teal-400 transition-colors cursor-pointer">
                • Посудомоечные машины
              </li>
              <li className="hover:text-teal-400 transition-colors cursor-pointer">
                • Сушильные машины
              </li>
              <li className="hover:text-teal-400 transition-colors cursor-pointer">
                • Диагностика
              </li>
            </ul>
          </div>

          {/* Contact info */}
          <div>
            <h4 className="font-bold text-sm md:text-lg mb-3 md:mb-4 bg-gradient-to-r from-teal-400 to-emerald-400 bg-clip-text text-transparent">
              Контакты
            </h4>
            <ul className="space-y-2 md:space-y-3 text-xs md:text-sm text-gray-400">
              <li className="flex items-center group cursor-pointer">
                <i className="ri-phone-line mr-2 w-4 h-4 md:w-5 md:h-5 flex items-center justify-center group-hover:text-teal-400 transition-all duration-300"></i>
                <a href="tel:+77074325020" className="hover:text-teal-400 transition-colors">
                  +7 (707) 432-50-20
                </a>
              </li>
              <li className="flex items-center group cursor-pointer">
                <i className="ri-mail-line mr-2 w-4 h-4 md:w-5 md:h-5 flex items-center justify-center group-hover:text-teal-400 transition-all duration-300"></i>
                <a href="mailto:info@serv1ce.kz" className="hover:text-teal-400 transition-colors">
                  info@serv1ce.kz
                </a>
              </li>
              <li className="flex items-start group cursor-pointer">
                <i className="ri-map-pin-line mr-2 mt-0.5 w-4 h-4 md:w-5 md:h-5 flex items-center justify-center group-hover:text-teal-400 transition-all duration-300"></i>
                <span className="hover:text-teal-400 transition-colors">
                  Алматы, Казахстан
                </span>
              </li>
            </ul>
          </div>
        </div>

        {/* Footer bottom bar */}
        <div className="border-t border-gray-600/50 pt-6 md:pt-8">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-3 md:gap-4">
            <p className="text-xs md:text-sm text-gray-400">
              © 2015 serv1ce.kz. Все права защищены.
            </p>
            <span className="text-xs md:text-sm text-gray-400">
              by GevoRkyaN
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
}
